import { useCallback } from "react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { InputFieldNameText } from "../InputField/components/InputFieldNameText";
import { InputFieldDescriptionText } from "../InputField/components/InputFieldDescriptionText";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
  },
  radio: {
    "& .MuiSvgIcon-root": {
      width: 20,
      height: 20,
    },
    // Dark mode (additive): re-tone only the UNCHECKED radio icon so it reads on a
    // dark surface. The checked icon keeps its brand (secondary) color. Light
    // appearance is unchanged.
    "html.dark &:not(.Mui-checked) .MuiSvgIcon-root": {
      color: "hsl(var(--sds-muted-foreground))",
    },
  },
  label: {
    "& .MuiFormControlLabel-label": {
      fontSize: 14,
      color: "#707087",
    },
    // Dark mode (additive): re-tone the option label text; light unchanged.
    "html.dark & .MuiFormControlLabel-label": {
      color: "hsl(var(--sds-muted-foreground))",
    },
  },
}));

export type RadioButtonOption = {
  value: string;
  label: string;
  topText?: string;
  bottomText?: string;
  image?: string;
};

export type RadioButtonFieldProps = {
  className?: string;
  description?: string;
  error?: string;
  label: string;
  name: string;
  options: RadioButtonOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  cypressId?: string;
};

export const RadioButtonField = ({
  className,
  description,
  disabled,
  error,
  label,
  name,
  onBlur,
  onChange,
  options,
  value,
  required,
  cypressId,
}: RadioButtonFieldProps) => {
  const classes = useStyles();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <Box width="100%" className={className}>
      <Box className="inputLabelContainer">
        <InputFieldNameText>
          {label}
          {required && <span className="required">*</span>}
        </InputFieldNameText>
        {description && <InputFieldDescriptionText>{description}</InputFieldDescriptionText>}
      </Box>
      <Box className={classes.container} data-cypress-id={`${cypressId}Container`}>
        <RadioGroup aria-label={name} name={name} value={value} onChange={handleChange} onBlur={onBlur} row>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio disabled={disabled} className={classes.radio} />}
              label={option.label}
              className={classes.label}
              data-cypress-id={`${cypressId}`}
            />
          ))}
        </RadioGroup>
      </Box>
      {error && (
        <Box className="errorContainer" data-cypress-id={`${cypressId}Error`}>
          {error}
        </Box>
      )}
    </Box>
  );
};
